import { Trans, useLingui } from '@lingui/react/macro'
import { useNavigate } from '@tanstack/react-router'
import { CantileverButton } from 'components/CantileverButton'
import { NamePromptModal } from 'components/NamePromptModal'
import { CreateCaseModal } from 'features/cases'
import { generateEntrySlug } from 'features/rolodex'
import { useResolvedChart, useScatterplotContext } from 'features/timeSeries'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import {
  loadPointAnnotationsFromChart,
  loadRulerConfigsFromChart,
} from 'features/timeSeries/utils/chartConfigLoaders'
import {
  serializeDateConfig,
  serializePointAnnotationConfigs,
  serializeRulerConfigs,
  serializeSourceConfigs,
} from 'features/timeSeries/utils/chartSerialization'
import { serializeDataProcessingConfigs } from 'features/timeSeries/utils/dataProcessingSerialization'
import { formatTimeAgo } from 'features/timeSeries/utils/timeUtils'
import { ArrowLeft, Sparkles } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createProcessDataOrgSiteSearch } from 'routes/process-data/-searchParams'
import styled from 'styled-components'
import { mobileInputTextStyles } from 'styles/mobileInputTextStyles'
import { RichTextContent, RolodexEntryType } from 'types'

const HeaderContainer = styled.header`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.5rem 1rem;
  background: ${({ theme }) => theme.forecast.cardBackground};
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  gap: 0.5rem;
  z-index: 10;
`

const ChartNameInput = styled.input`
  min-width: 10rem;
  max-width: 24rem;
  flex: 1 0 12rem;
  height: 2rem;
  padding: 0 0.5rem;
  background: transparent;
  color: ${({ theme }) => theme.forecast.defaultText};
  border: 1px solid transparent;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-weight: 500;

  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.forecast.border};
    background: ${({ theme }) => theme.forecast.pageBackground1};
    outline: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.forecast.tertiaryText};
    font-style: italic;
  }
  ${mobileInputTextStyles}
`

const SavingContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  justify-content: center;
  gap: 0.125rem;
`

const SavingTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  white-space: nowrap;
  cursor: default;
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  height: 2rem;
  padding: 0 0.5rem;
`

const UnsavedChangesText = styled.div`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.6rem;
`

const LastSavedText = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.6rem;
`

const RightSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`

const HeaderButton = styled(CantileverButton)`
  flex-basis: content;
`

const RevertButton = styled(HeaderButton)`
  border-radius: 0.25rem 0 0 0.25rem;
`

const SaveButton = styled(HeaderButton)`
  border-radius: 0 0.25rem 0.25rem 0;
`

interface DashboardHeaderProps {
  toggleChatbot?: () => void
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  toggleChatbot,
}) => {
  const { t } = useLingui()

  const {
    saveChart,
    revertChanges,
    hasUnsavedChanges,
    lastSavedAt,
    siteID,
    organizationID,
    chartID,
    chartName,
    setChartName,
    transient,
  } = useScatterplotContext()

  const navigate = useNavigate()
  const [isCaseModalOpen, setIsCaseModalOpen] = useState(false)
  const [isNamePromptOpen, setIsNamePromptOpen] = useState(false)
  const saveAfterNameRef = useRef(false)
  const { chart, isLoading: isLoadingChart } = useResolvedChart(
    transient ? undefined : chartID
  )
  const [currentTime, setCurrentTime] = useState(() => new Date())
  const timeAgoText = formatTimeAgo(lastSavedAt, currentTime)

  const handleSaveClick = useCallback(() => {
    if (!chartName.trim()) {
      saveAfterNameRef.current = true
      setIsNamePromptOpen(true)
      return
    }
    void saveChart()
  }, [chartName, saveChart])

  const handleNameConfirm = useCallback(
    (name: string) => {
      setChartName(name)
      setIsNamePromptOpen(false)
    },
    [setChartName]
  )

  // Fire the deferred save once chartName actually reflects the entered value.
  useEffect(() => {
    if (saveAfterNameRef.current && chartName.trim()) {
      saveAfterNameRef.current = false
      void saveChart()
    }
  }, [chartName, saveChart])

  const { chartCopyContent, chartCopyEntry } = useMemo(() => {
    if (!chart) {
      return { chartCopyContent: undefined, chartCopyEntry: undefined }
    }

    const newChartID = generateEntrySlug(RolodexEntryType.CHART)
    const chartName = chart.name || 'Untitled Chart'

    // This HTML is serialized case content; its data attributes are schema keys.
    // eslint-disable-next-line lingui/no-unlocalized-strings -- Keep serialized HTML attributes stable.
    const content = `
    <span data-lexical-slash-command="true" data-entry-type="chart" data-entry-id="${newChartID}" data-entry-name="${chartName}" data-metadata="{&quot;siteId&quot;:&quot;${siteID}&quot;,&quot;orgId&quot;:&quot;${organizationID}&quot;}"></span>
    ` as RichTextContent

    const entry = {
      id: newChartID,
      type: RolodexEntryType.CHART,
      data: {
        name: chart.name,
        description: chart.description,
        source_configs: serializeSourceConfigs(chart.sourceConfigs),
        y_axis_mode: chart.yAxisMode,
        date_config: serializeDateConfig(chart.dateConfig),
        // Serialize, don't spread. The schema is transform-based: reads hand
        // back camelCase and writes must send snake_case, so spreading the
        // resolved chart straight through wrote camelCase that silently failed
        // validation on the next read -- a copied chart lost its data
        // processing configs with no error.
        ...(chart.dataProcessingConfigs && {
          data_processing_configs: serializeDataProcessingConfigs(
            chart.dataProcessingConfigs
          ),
        }),
        // Round-trip through the loaders rather than re-deriving the runtime
        // shape here: they are the single source of truth for schema-output ->
        // runtime, and pixel fields are intentionally not persisted.
        ...(chart.rulerConfigs && {
          ruler_configs: serializeRulerConfigs(
            loadRulerConfigsFromChart(chart)
          ),
        }),
        ...(chart.pointAnnotationConfigs && {
          point_annotation_configs: serializePointAnnotationConfigs(
            loadPointAnnotationsFromChart(chart)
          ),
        }),
        ...(chart.interactionMode && {
          interaction_mode: chart.interactionMode,
        }),
      },
      tags: {
        site: siteID,
        organization: organizationID,
      },
    }

    return { chartCopyContent: content, chartCopyEntry: entry }
  }, [chart, siteID, organizationID])

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date())
    }, 60000)

    return () => clearInterval(interval)
  }, [])

  return (
    <>
      <HeaderContainer>
        <ChartTooltip label={t`Back to charts`}>
          <HeaderButton
            iconOnly
            ghost
            onClick={() =>
              navigate({
                to: '/process-data',
                search: createProcessDataOrgSiteSearch({
                  orgId: organizationID || undefined,
                  siteId: siteID,
                }),
              })
            }>
            <ArrowLeft width={'1.25rem'} height={'1.25rem'} />
          </HeaderButton>
        </ChartTooltip>
        <ChartNameInput
          value={chartName}
          placeholder={t`Untitled Chart`}
          aria-label={t`Chart name`}
          onChange={(event) => setChartName(event.target.value)}
        />
        <SavingContainer>
          {hasUnsavedChanges ? (
            <>
              <RevertButton
                onClick={revertChanges}
                disabled={!hasUnsavedChanges}>
                <Trans>Revert</Trans>
              </RevertButton>
              <SavingTextContainer>
                <UnsavedChangesText>
                  <Trans>Chart has unsaved changes</Trans>
                </UnsavedChangesText>
                <LastSavedText>{timeAgoText}</LastSavedText>
              </SavingTextContainer>
              <SaveButton
                onClick={handleSaveClick}
                disabled={!hasUnsavedChanges}>
                <Trans>Save</Trans>
              </SaveButton>
            </>
          ) : (
            <LastSavedText>{timeAgoText}</LastSavedText>
          )}
        </SavingContainer>
        <RightSection>
          <HeaderButton
            onClick={() => setIsCaseModalOpen(true)}
            disabled={isLoadingChart}>
            <Trans>Create Case</Trans>
          </HeaderButton>
          {toggleChatbot && (
            <HeaderButton iconOnly primary onClick={toggleChatbot}>
              <Sparkles width={'1.25rem'} height={'1.25rem'} />
            </HeaderButton>
          )}
        </RightSection>
      </HeaderContainer>
      <CreateCaseModal
        isOpen={isCaseModalOpen}
        onClose={() => setIsCaseModalOpen(false)}
        siteID={siteID}
        organizationID={organizationID}
        initialCaseContent={chartCopyContent}
        additionalEntriesToPatch={chartCopyEntry ? [chartCopyEntry] : undefined}
      />
      {isNamePromptOpen && (
        <NamePromptModal
          title={t`Name this chart`}
          subtitle={t`Give the chart a name before saving so it can be found later.`}
          initialValue={chartName}
          confirmLabel={t`Save chart`}
          onConfirm={handleNameConfirm}
          onCancel={() => {
            saveAfterNameRef.current = false
            setIsNamePromptOpen(false)
          }}
        />
      )}
    </>
  )
}
